import type { ParkingSpot, SeekerPin, RentPin, Filters, City, GeocodeResult, CommentPinType, PublicComment } from '../types';

const BASE_URL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || '');

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export const api = {
  // Spots
  getSpots: (params?: Partial<Filters & { lat?: number; lng?: number; radius?: number }>) =>
    fetchApi<{ data: ParkingSpot[] }>(`/api/spots?${buildQuery(params)}`).then((r) => r.data),

  getSpot: (id: string) =>
    fetchApi<{ data: ParkingSpot }>(`/api/spots/${id}`).then((r) => r.data),

  createSpot: (data: Omit<ParkingSpot, 'id' | 'created_at' | 'interest_count' | 'comment_count'> & { email: string; phone: string }) =>
    fetchApi<{ data: ParkingSpot; message: string }>('/api/spots', {
      method: 'POST',
      body: JSON.stringify(data),
    }).then((r) => r.data),

  deleteSpot: (id: string) => fetchApi<{ message: string }>(`/api/spots/${id}`, { method: 'DELETE' }),

  // Seekers
  getSeekers: (params?: { lat?: number; lng?: number; radius?: number }) =>
    fetchApi<{ data: SeekerPin[] }>(`/api/seekers?${buildQuery(params)}`).then((r) => r.data),

  getSeeker: (id: string) =>
    fetchApi<{ data: SeekerPin }>(`/api/seekers/${id}`).then((r) => r.data),

  createSeeker: (data: Omit<SeekerPin, 'id' | 'created_at' | 'comment_count'> & { email: string; phone: string }) =>
    fetchApi<{ data: SeekerPin; message: string }>('/api/seekers', {
      method: 'POST',
      body: JSON.stringify(data),
    }).then((r) => r.data),

  deleteSeeker: (id: string) => fetchApi<{ message: string }>(`/api/seekers/${id}`, { method: 'DELETE' }),

  // Rent pins
  getRentPins: (params?: { lat?: number; lng?: number; radius?: number; vehicle_type?: string }) =>
    fetchApi<{ data: RentPin[] }>(`/api/rent-pins?${buildQuery(params)}`).then((r) => r.data),

  getRentPin: (id: string) =>
    fetchApi<{ data: RentPin }>(`/api/rent-pins/${id}`).then((r) => r.data),

  createRentPin: (data: Omit<RentPin, 'id' | 'created_at' | 'comment_count'>) =>
    fetchApi<{ data: RentPin; message: string }>('/api/rent-pins', {
      method: 'POST',
      body: JSON.stringify(data),
    }).then((r) => r.data),

  // Stats
  getOverallStats: () => fetchApi<{ data: { parking_spots: number; seeker_pins: number; rent_pins: number; matches: number } }>('/api/stats/overall').then((r) => r.data),
  getMetroCities: () => fetchApi<{ data: City[] }>('/api/cities/metros').then((r) => r.data),

  // Feedback
  submitFeedback: (message: string, contact?: string) =>
    fetchApi<{ message: string }>('/api/feedback', {
      method: 'POST',
      body: JSON.stringify({ message, contact }),
    }),

  // Reports
  reportPin: (
    pinType: 'parking_spot' | 'rent_pin',
    pinId: string,
    reasonCategory:
      | 'inaccurate_location'
      | 'wrong_info'
      | 'duplicate'
      | 'not_parking'
      | 'spam'
      | 'other'
  ) =>
    fetchApi<{ message: string }>('/api/reports', {
      method: 'POST',
      body: JSON.stringify({
        pin_type: pinType,
        pin_id: pinId,
        reason: reasonCategory,
      }),
    }),

  // Geocoding
  geocode: (query: string) =>
    fetchApi<{ data: GeocodeResult[] }>(`/api/geocode?${buildQuery({ q: query })}`).then((r) => r.data),

  // Interests
  createInterest: (payload: {
    parking_spot_id: string;
    seeker_name?: string;
    email: string;
    phone: string;
    message?: string;
  }) =>
    fetchApi<{ message: string; result: string; email_sent: boolean }>('/api/interests', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getInterestCount: (spotId: string) =>
    fetchApi<{ count: number }>(`/api/interests/count?${buildQuery({ pin_type: 'parking_spot', pin_id: spotId })}`).then((r) => r.count),

  // Comments
  getComments: (pinType: CommentPinType, pinId: string, limit?: number) =>
    fetchApi<{ data: PublicComment[] }>(`/api/comments?${buildQuery({ pin_type: pinType, pin_id: pinId, limit })}`).then((r) => r.data),

  createComment: (pinType: CommentPinType, pinId: string, body: string, displayName?: string) =>
    fetchApi<{ data: PublicComment; message: string; links_stripped: number }>('/api/comments', {
      method: 'POST',
      body: JSON.stringify({
        pin_type: pinType,
        pin_id: pinId,
        body,
        display_name: displayName || undefined,
      }),
    }).then((r) => r.data),

  reportComment: (commentId: string) =>
    fetchApi<{ message: string }>(`/api/comments/${commentId}/report`, {
      method: 'POST',
    }),
};

function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return '';
  const filtered: Record<string, string> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        if (value.length > 0) filtered[key] = value.join(',');
      } else {
        filtered[key] = String(value);
      }
    }
  });
  return new URLSearchParams(filtered).toString();
}
